import React, { useRef, useState } from 'react';
import { useAppContext } from 'components/Context/AppContext';
import { Button, InputField, SelectField } from 'components/Global';
import { ReactComponent as BannerIcon } from 'icons/banner-filled-icon.svg';
import { ReactComponent as TrashIcon } from 'icons/trash-icon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { createBanner, getDetailBanner, updateBanner, uploadBannerImage } from 'features/banner/actions';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LanguageEditor from 'components/HomeComponent/Content/TermAndConditionComponent/LanguageEditor';
import { useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { getAllLanguage } from 'features/language/actions';
import Tabs, { TabPane } from 'components/Global/Tabs';
import { extractDriveFileId, formatGoogleDriveUrl } from 'utils/helpers';
import { trackEvent } from 'utils/analytics';
import { getConfig } from 'config/runtime';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';

const imageUrl = getConfig('REACT_APP_IMAGES');

const EditBannerModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { bannerId } = useParams();

  const targetRef = useRef(null);

  const { setShowSpinner, showToast } = useAppContext();
  const user = useSelector((state) => state.user.profile);
  const { data: languageData } = useSelector((state) => state.language);
  const isEditing = bannerId !== undefined;
  const currentBanner = useSelector((state) => state.banner.bannerDetail);

  const isBannerUrlEnabled = useFliptBoolean('FLAG_FE_ADD_BANNER_URL_FIELD', false, user?.id || 'anonymous');

  const [hasPeriod, setHasPeriod] = useState(false);
  const [selectedImage, setSelectedImage] = useState({
    name: '',
    url: '',
    size: 0,
  });
  const [bannerData, setBannerData] = useState({
    start_date: '',
    end_date: '',
    image: '',
    sort: 0,
    link: '',
    secondary_link: '',
    descriptions: [],
    available: false,
  });
  const [activeTab, setActiveTab] = useState('Indonesia');
  const [file, setFile] = useState(null);
  const [urlError, setUrlError] = useState('');

  useEffect(() => {
    dispatch(getAllLanguage());
  }, []);

  useEffect(() => {
    if (!bannerId) return;

    dispatch(getDetailBanner(bannerId));
  }, [bannerId]);

  useEffect(() => {
    if (!Object.keys(currentBanner).length || !isEditing) return;

    setBannerData({
      id: currentBanner.id,
      start_date: formatBannerDate(currentBanner.start_date),
      end_date: formatBannerDate(currentBanner.end_date),
      image: currentBanner.image,
      link: currentBanner.link,
      secondary_link: currentBanner.secondary_link || '',
      sort: currentBanner.sort,
      available: currentBanner.available,
      descriptions: currentBanner?.descriptions
        ? currentBanner.descriptions.map((item) => ({
            id: item.language_id,
            value: item.description,
            defaultId: item.id,
          }))
        : [],
    });

    setHasPeriod(formatBannerDate(currentBanner.start_date) === '');
  }, [currentBanner]);

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    navigate('/banner');
  };

  useEffect(() => {
    if (!Object.keys(languageData).length || !languageData.data?.length || isEditing) return;

    const formattedDesc = languageData.data.map((item) => ({ id: item.id, value: '' }));
    setBannerData((prev) => ({ ...prev, descriptions: formattedDesc }));
  }, [languageData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const secondaryLink = sanitizeUrl(bannerData.secondary_link);

    if (isBannerUrlEnabled && secondaryLink !== '' && !isValidUrl(secondaryLink)) {
      setUrlError(INVALID_URL_MESSAGE);
      showToast({
        message: 'URL Banner tidak valid. Harap masukkan URL yang benar.',
        type: 'error',
      });
      return;
    }

    if (isEditing) {
      // Update existing banner
      const payload = {
        id: bannerId,
        data: {
          available: bannerData.available,
          descriptions: bannerData.descriptions.length
            ? bannerData.descriptions.map((item) => ({ id: item.defaultId, description: item.value }))
            : undefined,
          ...(isBannerUrlEnabled ? { secondary_link: secondaryLink } : {}),
        },
      };

      try {
        trackEvent('banner_updated', { banner_id: bannerId, source: 'edit_banner_modal' });
        await dispatch(updateBanner(payload)).unwrap();
        showToast({ type: 'success', message: 'Berhasil Mengubah Data' });
        handleClose();
      } catch (error) {
        showToast({ type: 'error', message: 'Gagal Mengubah Data' });
      } finally {
        setShowSpinner(false);
      }
    } else {
      // Create new banner
      if (selectedImage.size >= 1000000) {
        showToast({
          message: 'Ukuran file tidak boleh lebih dari 1MB!',
          type: 'error',
        });
        return;
      }

      if (file === null && bannerData.link.trim() === '') {
        showToast({
          message: 'Harap pilih gambar atau masukkan link gambar terlebih dahulu.',
          type: 'error',
        });
        return;
      }

      if (!hasPeriod && (bannerData.start_date === '' || bannerData.end_date === '')) {
        showToast({
          message: 'Harap lengkapi periode banner',
          type: 'error',
        });
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      const data = {
        ...bannerData,
        secondary_link: secondaryLink,
        descriptions: bannerData.descriptions.map((item) => ({ language_id: item.id, description: item.value })),
      };

      if (isBannerUrlEnabled) {
        data.secondary_link = secondaryLink;
      } else {
        delete data.secondary_link;
      }

      try {
        setShowSpinner(true);
        if (file === null) {
          trackEvent('banner_created', { source: 'edit_banner_modal' });
          await dispatch(createBanner({ data: { ...data, image: '' } })).unwrap();
        } else {
          trackEvent('banner_image_uploaded', { source: 'edit_banner_modal' });
          await dispatch(uploadBannerImage({ payload: formData, data })).unwrap();
        }
        showToast({ type: 'success', message: 'Berhasil Menambahkan Data' });
      } catch (error) {
        showToast({ type: 'error', message: 'Gagal Menambahkan Banner' });
      } finally {
        setShowSpinner(false);
        handleClose();
      }
    }
  };

  const handleClearImage = () => {
    setSelectedImage({ name: '', url: '', size: 0 });
    setFile(null);
  };

  const onImageChange = (e) => {
    e.preventDefault();
    const { files } = e.target;
    if (files.length === null) return;

    const image = files[0];
    setFile(image);
    const previewImage = URL.createObjectURL(image);
    setSelectedImage(() => ({ name: image?.name, url: previewImage, size: image?.size }));
  };

  const handleChangeLink = (e) => {
    const input = e.target.value;

    const fileId = extractDriveFileId(input);
    if (fileId) {
      // const directUrl = `https://drive.google.com/thumbnail?id=${fileId}`;
      const directUrl = `https://drive.google.com/uc?export=view&id=${fileId}`;
      setBannerData((prev) => ({ ...prev, link: directUrl }));
    } else {
      setBannerData((prev) => ({ ...prev, link: null }));
    }
  };

  const handleDescriptionChange = (updatedDescriptions) => {
    setBannerData((prev) => ({
      ...prev,
      descriptions: prev.descriptions.map((desc) => {
        const updated = updatedDescriptions.find((u) => u.id === desc.language_id);

        if (updated) {
          return { ...desc, description: updated.value };
        }
        return desc;
      }),
    }));
  };

  const handleChangeUrl = (e) => {
    const url = sanitizeUrl(e.target.value);

    setUrlError(url === '' || isValidUrl(url) ? '' : INVALID_URL_MESSAGE);
    setBannerData((prev) => ({ ...prev, secondary_link: url }));
  };

  return (
    <div className="banner-modal-overlay" onClick={handleOverlayClick}>
      <div className="banner-modal-container">
        <div className="banner-modal-header">
          <div className="title">
            <BannerIcon fill="#009EF7" width="25px" height="25px" />
            <h2>Banner</h2>
          </div>

          {isEditing && (
            <div className="banner-detail__status-info">
              {bannerData.available ? (
                <span className="banner-status-badge banner-status-badge--active">Sedang Aktif</span>
              ) : (
                <span className="banner-status-badge banner-status-badge--blocked">Tidak Aktif</span>
              )}
              <div className="user__toggle-status">
                <label className="switch">
                  <input
                    type="checkbox"
                    role="switch"
                    checked={bannerData.available}
                    onChange={() => setBannerData((prev) => ({ ...prev, available: !bannerData.available }))}
                  />
                  <span className="slider slider-banner"></span>
                </label>
              </div>
            </div>
          )}
        </div>
        <form className="banner-modal" onSubmit={handleSubmit}>
          <div className="banner-modal__content">
            <div className="banner-modal__content--left">
              <div className="banner-modal__input-wrapper">
                <InputField
                  value={bannerData.start_date || ''}
                  disable={hasPeriod || isEditing}
                  required={!hasPeriod}
                  label="Tanggal Mulai"
                  placeholder="Pilih Tanggal Mulai"
                  type="date"
                  onChange={(e) => setBannerData((prev) => ({ ...prev, start_date: e.target.value }))}
                />

                <div className="banner-modal__checkbox-periode">
                  <input
                    type="checkbox"
                    name="period"
                    id="period"
                    checked={hasPeriod}
                    onChange={isEditing ? undefined : (e) => setHasPeriod(e.target.checked)}
                    disabled={isEditing && !hasPeriod}
                  />
                  <label htmlFor="period">Banner tidak berperiode</label>
                </div>
              </div>

              <div className="g-input banner-modal__input">
                <label htmlFor="banner" className="input-text-title">
                  Banner
                </label>
                <section className="field-wrapper with-icon">
                  <input
                    type="file"
                    name="upload-image"
                    id="upload-image"
                    accept="image/*"
                    hidden
                    ref={targetRef}
                    onChange={onImageChange}
                  />
                  <div className="upload__wrapper">
                    {!isEditing && (
                      <div className="upload__header">
                        <div className="upload__header-left">
                          <button
                            className="upload__button"
                            onClick={(e) => {
                              e.preventDefault();
                              targetRef.current.click();
                            }}
                          >
                            Browse..
                          </button>
                          {selectedImage.name ? <p>{selectedImage.name}</p> : null}
                        </div>
                        {selectedImage.name ? (
                          <div className="upload__header-right" onClick={handleClearImage}>
                            <TrashIcon width="24px" height="24px" />
                          </div>
                        ) : null}
                      </div>
                    )}

                    {selectedImage.url ? (
                      <figure className="upload__thumbnail">
                        <img src={selectedImage.url} alt="banner-image" />
                      </figure>
                    ) : null}

                    {isEditing && bannerData.image ? (
                      <figure className="upload__thumbnail">
                        <img src={imageUrl + bannerData.image} alt="banner-image" />
                      </figure>
                    ) : null}

                    {isEditing && !bannerData.image && bannerData.link ? (
                      <figure className="upload__thumbnail">
                        <img src={formatGoogleDriveUrl(bannerData.link)} alt="banner-image" />
                      </figure>
                    ) : null}
                  </div>
                </section>
              </div>
              <div className="banner-modal__link-wrapper">
                <div className="link-banner-wrapper">
                  <label htmlFor="link-banner" className="input-link-label">
                    Link Banner
                  </label>
                  <div>
                    <span>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M6 8.5C6.14167 8.5 6.2605 8.452 6.3565 8.356C6.4525 8.26 6.50033 8.14133 6.5 8V6C6.5 5.85833 6.452 5.73967 6.356 5.644C6.26 5.54833 6.14133 5.50033 6 5.5C5.85867 5.49967 5.74 5.54767 5.644 5.644C5.548 5.74033 5.5 5.859 5.5 6V8C5.5 8.14167 5.548 8.2605 5.644 8.3565C5.74 8.4525 5.85867 8.50033 6 8.5ZM6 4.5C6.14167 4.5 6.2605 4.452 6.3565 4.356C6.4525 4.26 6.50033 4.14133 6.5 4C6.49967 3.85867 6.45167 3.74 6.356 3.644C6.26033 3.548 6.14167 3.5 6 3.5C5.85833 3.5 5.73967 3.548 5.644 3.644C5.54833 3.74 5.50033 3.85867 5.5 4C5.49967 4.14133 5.54767 4.26017 5.644 4.3565C5.74033 4.45283 5.859 4.50067 6 4.5ZM6 11C5.30833 11 4.65833 10.8687 4.05 10.606C3.44167 10.3433 2.9125 9.98717 2.4625 9.5375C2.0125 9.08783 1.65633 8.55867 1.394 7.95C1.13167 7.34133 1.00033 6.69133 1 6C0.999667 5.30867 1.131 4.65867 1.394 4.05C1.657 3.44133 2.01317 2.91217 2.4625 2.4625C2.91183 2.01283 3.441 1.65667 4.05 1.394C4.659 1.13133 5.309 1 6 1C6.691 1 7.341 1.13133 7.95 1.394C8.559 1.65667 9.08817 2.01283 9.5375 2.4625C9.98683 2.91217 10.3432 3.44133 10.6065 4.05C10.8698 4.65867 11.001 5.30867 11 6C10.999 6.69133 10.8677 7.34133 10.606 7.95C10.3443 8.55867 9.98817 9.08783 9.5375 9.5375C9.08683 9.98717 8.55767 10.3435 7.95 10.6065C7.34233 10.8695 6.69233 11.0007 6 11ZM6 10C7.11667 10 8.0625 9.6125 8.8375 8.8375C9.6125 8.0625 10 7.11667 10 6C10 4.88333 9.6125 3.9375 8.8375 3.1625C8.0625 2.3875 7.11667 2 6 2C4.88333 2 3.9375 2.3875 3.1625 3.1625C2.3875 3.9375 2 4.88333 2 6C2 7.11667 2.3875 8.0625 3.1625 8.8375C3.9375 9.6125 4.88333 10 6 10Z"
                          fill="#009EF7"
                        />
                      </svg>
                    </span>
                    <span>Pastikan Gambar pada link dapat diakses oleh umum</span>
                  </div>
                </div>

                <input
                  type="text"
                  name="link-banner"
                  id="link-banner"
                  className="input-link"
                  disabled={isEditing}
                  onChange={handleChangeLink}
                />

                {bannerData.link && (
                  <div className="banner-modal__link-preview">
                    <figure className="upload__thumbnail">
                      <img src={formatGoogleDriveUrl(bannerData.link)} alt="banner-image" />
                    </figure>
                  </div>
                )}
              </div>
            </div>
            <div className="banner-modal__content--right">
              <InputField
                disable={hasPeriod || isEditing}
                required={!hasPeriod}
                value={bannerData.end_date || ''}
                label="Tanggal Berakhir"
                placeholder="Pilih Tanggal Berakhir"
                type="date"
                onChange={(e) => setBannerData((prev) => ({ ...prev, end_date: e.target.value }))}
              />
              <div className="banner-modal__input-promo">
                <div className="banner-modal__input-promo-label">
                  <label htmlFor="choosePromo">Pilih Promo</label>
                  <div className="banner-modal__checkbox-promo">
                    <input type="checkbox" name="period" id="promo" disabled />
                    <label htmlFor="promo">Hubungkan dengan promo</label>
                  </div>
                </div>
                <SelectField data={DUMMY_PROMO} disabled />
              </div>
              <InputField
                value={bannerData.sort || ''}
                label="Urutan Banner"
                placeholder="0"
                type="number"
                min={0}
                onChange={(e) => setBannerData((prev) => ({ ...prev, sort: parseInt(e.target.value) }))}
                disable={isEditing}
              />
            </div>

            {isBannerUrlEnabled ? (
              <div className="banner-modal__url-link-wrapper">
                <div className="url-banner-wrapper">
                  <label htmlFor="url-banner" className="input-link-label">
                    URL Banner
                  </label>
                  <div>
                    <span>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M6 8.5C6.14167 8.5 6.2605 8.452 6.3565 8.356C6.4525 8.26 6.50033 8.14133 6.5 8V6C6.5 5.85833 6.452 5.73967 6.356 5.644C6.26 5.54833 6.14133 5.50033 6 5.5C5.85867 5.49967 5.74 5.54767 5.644 5.644C5.548 5.74033 5.5 5.859 5.5 6V8C5.5 8.14167 5.548 8.2605 5.644 8.3565C5.74 8.4525 5.85867 8.50033 6 8.5ZM6 4.5C6.14167 4.5 6.2605 4.452 6.3565 4.356C6.4525 4.26 6.50033 4.14133 6.5 4C6.49967 3.85867 6.45167 3.74 6.356 3.644C6.26033 3.548 6.14167 3.5 6 3.5C5.85833 3.5 5.73967 3.548 5.644 3.644C5.54833 3.74 5.50033 3.85867 5.5 4C5.49967 4.14133 5.54767 4.26017 5.644 4.3565C5.74033 4.45283 5.859 4.50067 6 4.5ZM6 11C5.30833 11 4.65833 10.8687 4.05 10.606C3.44167 10.3433 2.9125 9.98717 2.4625 9.5375C2.0125 9.08783 1.65633 8.55867 1.394 7.95C1.13167 7.34133 1.00033 6.69133 1 6C0.999667 5.30867 1.131 4.65867 1.394 4.05C1.657 3.44133 2.01317 2.91217 2.4625 2.4625C2.91183 2.01283 3.441 1.65667 4.05 1.394C4.659 1.13133 5.309 1 6 1C6.691 1 7.341 1.13133 7.95 1.394C8.559 1.65667 9.08817 2.01283 9.5375 2.4625C9.98683 2.91217 10.3432 3.44133 10.6065 4.05C10.8698 4.65867 11.001 5.30867 11 6C10.999 6.69133 10.8677 7.34133 10.606 7.95C10.3443 8.55867 9.98817 9.08783 9.5375 9.5375C9.08683 9.98717 8.55767 10.3435 7.95 10.6065C7.34233 10.8695 6.69233 11.0007 6 11ZM6 10C7.11667 10 8.0625 9.6125 8.8375 8.8375C9.6125 8.0625 10 7.11667 10 6C10 4.88333 9.6125 3.9375 8.8375 3.1625C8.0625 2.3875 7.11667 2 6 2C4.88333 2 3.9375 2.3875 3.1625 3.1625C2.3875 3.9375 2 4.88333 2 6C2 7.11667 2.3875 8.0625 3.1625 8.8375C3.9375 9.6125 4.88333 10 6 10Z"
                          fill="#009EF7"
                        />
                      </svg>
                    </span>
                    <span>Memiliki fungsi untuk mengarahkan Banner ke link yang diinputkan</span>
                  </div>
                </div>

                <input
                  type="text"
                  name="url-banner"
                  id="url-banner"
                  className="input-link"
                  value={bannerData.secondary_link || ''}
                  placeholder="URL Banner"
                  onChange={handleChangeUrl}
                  aria-invalid={Boolean(urlError)}
                />

                {urlError ? <p className="input-link-error">{urlError}</p> : null}
              </div>
            ) : null}

            <div className="banner-modal__description">
              <h3>Deskripsi</h3>

              {!Object.keys(languageData).length || !languageData.data.length ? null : (
                <Tabs activeTab={activeTab} setActiveTab={setActiveTab}>
                  {languageData.data.map((item) => (
                    <TabPane key={item.id} label={item.name}>
                      <LanguageEditor
                        lang={item}
                        allValue={bannerData.descriptions}
                        value={bannerData.descriptions?.find((val) => val.id === item.id)?.value}
                        onChange={(val) => handleDescriptionChange(val)}
                      />
                    </TabPane>
                  ))}
                </Tabs>
              )}
            </div>
          </div>

          <div className="about-us-buttons">
            <Button className="button" size="sm" width="192px" role="submit">
              {isEditing ? 'Simpan' : 'Tambah'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBannerModal;

const DUMMY_PROMO = [
  { id: '01', name: 'Promo 01' },
  { id: '02', name: 'Promo 02' },
  { id: '03', name: 'Promo 03' },
];

const INVALID_URL_MESSAGE = 'Format URL tidak valid.';

// URL Banner is optional, but it must never be sent with whitespace in it.
const sanitizeUrl = (value) => (value || '').replace(/\s/g, '');

const isValidUrl = (value) => {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

const formatBannerDate = (date) => {
  const invalidDates = ['2000-01-01T00:00:00Z', '3000-01-01T00:00:00Z'];

  if (!date || invalidDates.includes(date)) return '';

  return format(parseISO(date), 'yyyy-MM-dd');
};
